<script setup>
import { ref, computed, onMounted } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import SettingsPricingCard from '@/Components/Forms/SettingsPricingCard.vue'

const props = defineProps({
  user: Object,
  teachingMethods: Array,
  offerings: Array,
  pricingLimits: Object,
})

const loading = ref(false)
const successMessage = ref('')
const errors = ref({})

// Teaching methods selection - Initialize with user's methods
const selectedMethods = ref(props.user?.teachingMethods?.map(m => m.id) || [])

// Pricing state - default to average from TutorSettings
const goldPlanPrice = ref(props.pricingLimits?.gold?.average ?? Math.round(((props.pricingLimits?.gold?.min ?? 100) + (props.pricingLimits?.gold?.max ?? 200)) / 2))

/**
 * Whether this tutor offers the one product the site sells.
 *
 * This page used to list the four teaching methods behind them — a tutor read
 * "Group Tutoring" here and "Bindle Live" on the card immediately below it —
 * and worked out which plans to show by matching those names, so renaming a row
 * in the database changed a tutor's pricing. Each offering declares its tier and
 * the capability that identifies it.
 */
const offeredOfferings = computed(() =>
  (props.offerings || []).filter(offering => selectedMethods.value.includes(offering.primary_method_id))
)

const offersTier = (tier) => offeredOfferings.value.some(offering => offering.tier === tier)

const showGoldPlan = computed(() => offersTier('gold'))

// Load existing data
onMounted(() => {
  // Load existing pricing tiers
  const tiers = props.user?.pricingTiers || props.user?.pricing_tiers
  if (tiers && tiers.length > 0) {
    tiers.forEach(tier => {
      if (tier.tier === 'gold' || tier.tier === 'Gold') {
        goldPlanPrice.value = parseFloat(tier.price)
      }
    })
  }
})

// Effective slider limits — expand range if the current price falls outside TutorSettings bounds
const effectiveLimits = computed(() => ({
  gold: {
    min: Math.min(props.pricingLimits.gold.min, goldPlanPrice.value),
    max: Math.max(props.pricingLimits.gold.max, goldPlanPrice.value),
  },
}))

const savePricing = async () => {
  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        teaching_method_ids: selectedMethods.value,
        gold_plan_price: goldPlanPrice.value,
        offer_gold_plan: showGoldPlan.value,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key]) ? data.errors[key][0] : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Pricing updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- What the tutor offers, named the way the rest of the site names it -->
      <div class="bg-white rounded-2xl p-8 border-2 border-gray-200">
        <div class="mb-6">
          <h2 class="text-2xl font-bold text-gray-900 mb-2 flex items-center">
            <span class="material-symbols-outlined text-brand-blue mr-3">school</span>
            What You Offer
          </h2>
          <p class="text-gray-600">The products you teach on Bindle. The ones you offer are highlighted.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="offering in offerings"
            :key="offering.key"
            :class="[
              'relative inline-flex items-center gap-3 px-4 py-4 border-2 rounded-xl transition-all overflow-hidden',
              selectedMethods.includes(offering.primary_method_id)
                ? 'bg-white border-gray-300 shadow-sm'
                : 'bg-gray-50 border-gray-200 opacity-60'
            ]"
          >
            <!-- Top-right corner indicator for what is offered -->
            <div
              v-if="selectedMethods.includes(offering.primary_method_id)"
              class="absolute top-0 right-0 w-0 h-0"
              :style="{
                borderStyle: 'solid',
                borderWidth: '0 40px 40px 0',
                borderColor: `transparent ${offering.color} transparent transparent`
              }"
            ></div>

            <span
              v-if="selectedMethods.includes(offering.primary_method_id)"
              class="material-symbols-outlined absolute top-1 right-1 text-white text-sm z-10"
              style="font-size: 14px;"
            >
              check
            </span>

            <div class="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-brand-soft">
              <span class="material-symbols-outlined text-2xl text-brand">{{ offering.icon_name }}</span>
            </div>
            <div class="flex-1">
              <span class="text-base font-semibold text-gray-900 block">
                {{ offering.name }}
                <span
                  v-if="offering.coming_soon"
                  class="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                >
                  Coming soon
                </span>
              </span>
              <span class="text-xs text-gray-500">{{ offering.description }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pricing Cards - Stacked Full Width -->
      <div class="space-y-6">
        <!-- Gold Plan -->
        <SettingsPricingCard
          v-if="showGoldPlan"
          v-model:price="goldPlanPrice"
          title="1:1 Tutoring"
          subtitle="A weekly one-to-one lesson + your content library"
          icon="workspace_premium"
          :features="[
            { text: 'A weekly one-to-one lesson', bold: true },
            { text: 'Your videos and study material', bold: false },
            { text: 'A study plan built around the student', bold: false },
            { text: 'Direct messaging between lessons', bold: false },
          ]"
          :min-price="effectiveLimits.gold.min"
          :max-price="effectiveLimits.gold.max"
          :recommended-min="Math.round(pricingLimits.gold.average * 0.8)"
          :recommended-max="Math.round(pricingLimits.gold.average * 1.2)"
          border-color="border-amber-200"
          icon-bg-color="bg-gradient-to-br from-teal-50 to-teal-100"
          icon-text-color="text-amber-600"
          slider-accent-color="accent-amber-600"
          price-bg-color="bg-gradient-to-br from-teal-50 to-teal-100"
        />
      </div>

    </div>
  </SettingsLayout>
</template>
